import { graphql } from 'gatsby';
import React from 'react';
import { BlogPost } from '../../components/blog-post/post';
import { Layout } from '../../components/layout';
import { SEO } from '../../components/seo';
import { Post, PostSnippet } from '../../types';
import { mapPostData } from '../../module/map-post-data';
import { Helmet } from 'react-helmet';
import { WHITE_100 } from '@rategravity/own-up-component-library';
import { getSrc } from 'gatsby-plugin-image';
import BreadCrumbs from '../../components/breadcrumbs';

interface QueryData {
  post: Post;
  relatedPosts: {
    nodes: Post[];
  };
}

export const pageQuery = graphql`
  query BlogPostByID($id: String!, $recentPostsCategory: String!) {
    post: sanityPost(id: { eq: $id }) {
      id
      title
      description
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      _rawBody(resolveReferences: { maxDepth: 5 })
      slug {
        current
      }
      categories {
        title
      }
      mainImage {
        asset {
          gatsbyImageData(height: 500, width: 1280, layout: CONSTRAINED, placeholder: BLURRED)
        }
        alt
      }
      bodyUpdatedAt
      authors {
        name
        designation
        description
        mainImage {
          asset {
            gatsbyImageData(layout: CONSTRAINED, width: 100, height: 100, placeholder: BLURRED)
          }
          alt
        }
        slug {
          current
        }
        role
      }
      factCheckers {
        name
        designation
        description
        mainImage {
          asset {
            gatsbyImageData(layout: CONSTRAINED, width: 100, height: 100, placeholder: BLURRED)
          }
          alt
        }
        slug {
          current
        }
        role
      }
    }
    relatedPosts: allSanityPost(
      filter: {
        categories: { elemMatch: { title: { eq: $recentPostsCategory } } }
        id: { ne: $id }
        archive: { ne: true }
      }
      sort: { publishedAt: DESC }
      limit: 3
    ) {
      nodes {
        id
        title
        slug {
          current
        }
        mainImage {
          asset {
            gatsbyImageData(width: 411, layout: CONSTRAINED, placeholder: BLURRED, fit: MAX)
          }
          alt
        }
      }
    }
  }
`;

interface Crumb {
  title: string;
  path: string;
}

interface PageProps {
  data: QueryData;
  pageContext: {
    id: string;
    breadCrumbs: Crumb[];
  };
}

const HelmetComponent = () => (
  <Helmet>
    <style>
      {`
      main {
        background-color: ${WHITE_100}
      }`}
    </style>
  </Helmet>
);

const replaceDoubleQuotesWithSingle = (text: string) => {
  return text.replaceAll(/"/g, "'");
};

export const Page = ({ data, pageContext }: PageProps) => {
  const { breadCrumbs } = pageContext;
  const {
    id,
    title,
    excerpt,
    href,
    body,
    categories,
    authors,
    factCheckers,
    publishedAt,
    bodyUpdatedAt,
    img,
    imgAlt
  }: PostSnippet = mapPostData(data?.post);
  const relatedPostsData: PostSnippet[] = data?.relatedPosts?.nodes?.map(mapPostData);
  const imgUrl = getSrc(img);

  const schemaData = {
    headline: replaceDoubleQuotesWithSingle(title),
    datePublished: publishedAt,
    author: (authors?.length > 0 && authors[0]) || undefined,
    description: replaceDoubleQuotesWithSingle(excerpt),
    imageUrl: imgUrl || ''
  };

  return (
    <>
      <SEO
        title={title}
        description={excerpt || ''}
        imageUrl={imgUrl}
        schemaData={schemaData}
        breadCrumbs={breadCrumbs}
      />
      <Layout>
        <HelmetComponent />
        <BreadCrumbs breadCrumbs={breadCrumbs} />
        <BlogPost
          id={id}
          title={title}
          href={href}
          authors={authors}
          factCheckers={factCheckers}
          tags={categories || []}
          img={img}
          imgAlt={imgAlt}
          bodyUpdatedAt={bodyUpdatedAt}
          body={body}
          relatedPostsData={relatedPostsData}
        />
      </Layout>
    </>
  );
};

//eslint-disable-next-line import/no-default-export
export default Page;
